import { FC, useEffect } from 'react'
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { MeetingRoomOutlined } from '@mui/icons-material'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import HelpdeskListRow from 'components/Helpdesk/HelpdeskListRow'
import CreateReservation from 'components/CommonRoomReservations/CreateReservation'
import {
  getReservationGradient,
  getReservationStatusColors
} from 'components/Helpdesk/helpdesk.utils'
import { useCommonRoomsReservationsList } from 'services/commonRooms/commonRooms.hooks'
import { RESERVATION_STATUS_META } from 'services/commonRooms/commonRooms.consts'

// Common room reservations list ("Panel Klienta 3.0" Rezerwacje tab).
const CommonRoomReservationsList: FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { formatMessage } = useIntl()
  const { data: reservations = [], isFetching, isLoading } = useCommonRoomsReservationsList()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const reservationsList = [...reservations].sort(
    (a, b) => new Date(b.startDatetime).getTime() - new Date(a.startDatetime).getTime()
  )

  const selectedId = matchPath('/common-room-reservations/:id', pathname)?.params.id

  // On desktop, preselect the first reservation so the detail column is not empty.
  useEffect(() => {
    if (isDesktop && pathname === '/common-room-reservations' && reservationsList.length > 0) {
      navigate(`/common-room-reservations/${reservationsList[0].id}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, pathname, reservations])

  if (isFetching || isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isDesktop ? 0 : '12px' }}>
      {!reservationsList.length && (
        <HelpdeskEmptyState
          Icon={MeetingRoomOutlined}
          title={formatMessage({
            id: 'commonRoomReservations.emptyList',
            defaultMessage: 'Brak rezerwacji'
          })}
          subtitle={formatMessage({
            id: 'helpdesk.noReservationsHint',
            defaultMessage:
              'Nie masz jeszcze żadnych rezerwacji. Zarezerwuj salkę multimedialną lub inne udogodnienia dostępne w Twoim budynku.'
          })}
          action={
            <CreateReservation
              buttonSx={{
                backgroundColor: theme.r4r.navy,
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '14px',
                borderRadius: 0,
                padding: '12px 32px',
                boxShadow: 'none',
                ':hover': { backgroundColor: theme.r4r.ink, boxShadow: 'none' }
              }}
            />
          }
        />
      )}
      {reservationsList.map(({ commonRoomName, id, startDatetime, endDatetime, status }) => (
        <HelpdeskListRow
          key={id}
          onClick={() => navigate(`/common-room-reservations/${id}`)}
          selected={isDesktop && String(id) === selectedId}
          title={commonRoomName}
          subtitle={`${dayjs(startDatetime).format('DD.MM.YYYY')} · ${dayjs(
            startDatetime
          ).format('HH:mm')} – ${dayjs(endDatetime).format('HH:mm')}`}
          badgeLabel={formatMessage(RESERVATION_STATUS_META[status].i18n)}
          badgeColors={getReservationStatusColors(status, isDesktop)}
          iconSlot={
            <Box
              sx={{
                width: isDesktop ? '46px' : '48px',
                height: isDesktop ? '46px' : '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: isDesktop ? getReservationGradient(status) : theme.r4r.gradient
              }}>
              <MeetingRoomOutlined sx={{ color: '#fff', fontSize: isDesktop ? '20px' : '22px' }} />
            </Box>
          }
        />
      ))}
    </Box>
  )
}

export default CommonRoomReservationsList
